import { useMemo } from 'react';
import { useCartTotals, useClient, useSelectedClientScope, useTables } from '@/hooks';
import { normalizeParametroPrecoBase } from '@/shared';
import { PercentualFornecedorProps, TaxSubstitutionProps } from '@/types';
import { useCalcPrice } from '../calcPrices/useCalcPrice';
import { ComboCartLinePriceDeps } from './comboCartTotalsUtils';
import { useComboCartItems } from './useComboCartItems';

export const getCheckoutPaymentConditions = (scopedPayments: unknown, clientPayments: unknown): number[] => {
    const fromPayments = (pay: unknown) =>
        (Array.isArray(pay) ? pay : [])
            .map((item: { cod_condicao?: number; COD_CONDICAO?: number }) =>
                Number(item?.cod_condicao ?? item?.COD_CONDICAO)
            )
            .filter((value) => Number.isFinite(value) && value > 0);

    const scoped = fromPayments(scopedPayments);
    if (scoped.length > 0) return scoped;

    return fromPayments(clientPayments);
};

export const useComboCheckoutPriceDeps = (): ComboCartLinePriceDeps => {
    const { tables } = useTables();
    const { listaTributacao } = useCartTotals();
    const { selectedClient } = useClient();
    const selectedClientScope = useSelectedClientScope();
    const { handlePrices, findPrice } = useCalcPrice();
    const { combos, isComboCompleteById } = useComboCartItems();

    return useMemo((): ComboCartLinePriceDeps => {
        const codLoja = selectedClientScope?.COD_LOJA;
        const tabVenda = selectedClientScope?.COD_TABELA_VENDA;
        const condicoes = getCheckoutPaymentConditions(
            selectedClientScope?.COD_PAGAMENTO,
            selectedClient?.COD_PAGAMENTO
        );
        const percentual = (tables.current.percentual_fornecedor as PercentualFornecedorProps[]) ?? [];
        const listSubstTrib = (listaTributacao as TaxSubstitutionProps[]) ?? [];
        const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
        const percentualFornecedor = (Array.isArray(percentual) ? percentual : []).filter(
            (entry) => `${entry.NROTABVENDA}` === `${tabVenda}`
        );
        const parametroPreco = normalizeParametroPrecoBase(
            tables.current.parametro_preco_base,
            codLoja,
            tabVenda,
            condicoes
        );

        return {
            combos,
            isComboCompleteById,
            condicoes,
            handlePrices,
            findPrice,
            priceCalcParams: {
                parametroPreco,
                percentualFornecedor,
                listSubstTrib,
                parametroPrecoPromocao,
            },
        };
    }, [
        combos,
        isComboCompleteById,
        selectedClient,
        selectedClientScope,
        tables,
        listaTributacao,
        handlePrices,
        findPrice,
    ]);
};
